/* eslint-disable react/no-unused-state */
// TODO: Clean up state.tagFilter, state.tagValue, state.valueFocused
import {gql} from '@apollo/client';
import {groupBy, naturalSort, noop} from '@benchling/js-utils';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {abbreviateList, pluralize} from 'BenchlingUtil';
import {ActionOrigin, AnalyticsMetadataForContext, BaseAnalyticsMetadata} from 'constants/AnalyticsConstants';
import {StorableWithPermissions} from 'constants/SampleConstants';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import FileBrowserUtil, {TagTypesByName} from 'file-browser/FileBrowserUtil';
import {Tag} from 'folder-items/FileConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {UpdateContainerContentConcentrationsParam} from 'samples/ContainerDAO';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {
  bulkUpdateContainerContentConcentrationsWithDataMods,
  bulkUpdateContainerPropertiesWithDataMods,
} from 'samples/StorableActions';
import BulkUpdateExpirationDateOverrideModal from 'samples/toolbar/BulkUpdateExpirationDateOverrideModal';
import BulkUpdateFreezeThawCountModal from 'samples/toolbar/BulkUpdateFreezeThawCountModal';
import BulkUpdateRestrictedSampleUsersModal from 'samples/toolbar/BulkUpdateRestrictedSampleUsersModal';
import BulkUpdateRestrictionStatusModal from 'samples/toolbar/BulkUpdateRestrictionStatusModal';
import BulkUpdateSampleOwnersModal from 'samples/toolbar/BulkUpdateSampleOwnersModal';
import {ItemWithSampleRolesAndPermissions} from 'samples/toolbar/BulkUpdateSampleRolesModal';
import ConcentrationEditorModal from 'samples/toolbar/ConcentrationEditorModal';
import {MeasurementEditorModalSubmitArgs} from 'samples/toolbar/MeasurementEditorModal';
import QuantityEditorModal from 'samples/toolbar/QuantityEditorModal';
import {
  getUpdateRestrictedSampleUsersDisabledMessage,
  getUpdateSampleOwnersDisabledMessage,
  getUpdateSampleRestrictionStatusDisabledMessage,
} from 'samples/toolbar/UpdateSampleRolesMenuItems';
import {isProcessTaskOrOutput, isStorableSummary, isStudy, ListableItem} from 'search/SearchConstants';
import {hasFileSchemaSupport} from 'tag-schema/util/TagSchemaUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ClickOutWrapper from 'ui-library/ClickOutWrapper';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import omitKey from 'util/omitKey';
import {BasicEvent, MakeFieldsOptional, Optional} from 'util/Types';

import {ProcessOutputListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessOutputListingGraphQL';
import {ProcessTaskListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {StudyListingGraphQL} from '../../../../graphql-fragments/studies/__generated/StudyListingGraphQL';
import {TagEditorDropdownQuery, TagEditorDropdownQueryVariables} from './__generated/TagEditorDropdownQuery';
import getOptionDataFromTag from './getOptionDataFromTag';
import TagEditorModal, {TagEditorModalItem} from './TagEditorModal';

type ContainerInfo = ExtractByTypename<TagEditorDropdownQuery['containers'][number], 'ContainerNode'>;

type ContainerContent = ContainerInfo['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export const TAG_EDITOR_DROPDOWN_QUERY = gql`
  query TagEditorDropdownQuery($containerIds: [StrictString!]!) @skipAudit @engComponent(name: SCHEMAS) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        name
        sampleRoles {
          id
          isCurrentUserSampleOwner
          sampleOwners {
            ... on PartyInterface {
              id
              handle
              name
              avatarUrl
            }
          }
          restrictedSampleUsers {
            ... on PartyInterface {
              id
              handle
              name
              avatarUrl
            }
          }
        }
        restrictionStatus
        permissions {
          admin
          appendable
          canEditRestrictedSampleUsers
          canRestrictSamples
          canUnrestrictSamples
          readable
          writable
        }
        contents {
          id
          containedItem {
            ... on FolderItemInterface {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
        expirationInfo {
          id
          expirationDate
          isOverridden
          isExpired
          isTrackingEnabled
        }
        freezeThawInfo {
          id
          freezeThawCount
          isTrackingEnabled
        }
        parentStorable {
          ... on StorableInterface {
            id
            storableSchema {
              ... on PlateSchemaNode {
                id
                plateSchemaType
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// TODO: TagEditorDropdownProps['prop'] should ideally be parameterized using TS generics,
// but for now, the TagEditorDropdown operates on the union of all result types passed in by callers.
export type TagEditorItem =
  // StrorableToolbarContents
  | StorableWithPermissions
  // ExpandedToolbar
  | ListableItem
  // ProcessManagementToolbar
  | ProcessTaskListingGraphQL
  | ProcessOutputListingGraphQL;

export interface TagEditorDropdownProps {
  disabled?: boolean;
  finderId?: string;
  results: Array<TagEditorItem>;
  onSubmit: (newTag: Tag, selectedStorableIds?: Array<string>) => void;
  buttonStyle: ButtonStyle;
  actionOrigin: ActionOrigin;
  tooltipTitleWhenSelectable?: string;
  dropLeft?: boolean;
  // This callback aims to track item selections.
  onClickTracking?: (itemName: string) => void;
  /** Callback for item types not supported by this dropdown, but supported for bulk edits generally.
   * Leveraged to get a more useful error message when mixed types are selected. */
  itemHasAlternateEditor?: (item: ListableItem) => boolean;
}

interface QueriedProps {
  isQueryLoading: boolean;
  containerInfos: Array<ContainerInfo>;
}

interface TagEditorDropdownState {
  isOpen: boolean;
  tagFilter: string;
  tagValue: string;
  valueFocused: boolean;
}

const INITIAL_TAG_FILTER = '';
const INITIAL_TAG_VALUE = '';
// Unique values for bulk update menu item to avoid conflicts with user-created tag names
export const QUANTITY_MENU_ITEM_VALUE = Symbol('Quantity');
export const CONCENTRATION_MENU_ITEM_VALUE = Symbol('Concentration');
export const FREEZE_THAW_MENU_ITEM_VALUE = Symbol('Freeze/thaw count');
export const EXPIRATION_DATE_OVERRIDE_MENU_ITEM_VALUE = Symbol('Expiration date override');
export const SAMPLE_OWNERS_MENU_ITEM_VALUE = Symbol('Sample owners');
export const SAMPLE_RESTRICTION_STATUS_MENU_ITEM_VALUE = Symbol('Restriction status');
export const RESTRICTED_SAMPLE_USERS_MENU_ITEM_VALUE = Symbol('Authorized users');

interface TypeConfig {
  validateItemType: (id: string) => boolean;
  getSchemaId: (item: TagEditorItem) => string | null;
  getFields: (item: TagEditorItem) => Array<Optional<Tag>> | undefined;
}

const CONFIGS: Array<TypeConfig> = [
  {
    validateItemType: (id: string) => hasFileSchemaSupport(id),
    getSchemaId: (item: ListableItem) => item.tagSchema?.id ?? null,
    getFields: (item: ListableItem) => item.tags,
  },
  {
    validateItemType: (id: string) => APIUtil.isStorable(id),
    getSchemaId: (item: ListableItem) => (isStorableSummary(item) ? item.storableSchema.id : null),
    getFields: (item: ListableItem) => item.tags,
  },
  {
    validateItemType: (id: string) => APIUtil.isProcessTask(id) || APIUtil.isProcessOutput(id),
    getSchemaId: (item: TagEditorItem) => (isProcessTaskOrOutput(item) ? item.schema.id : null),
    getFields: (item: ProcessTaskListingGraphQL | ProcessOutputListingGraphQL) => item.fields,
  },
  {
    validateItemType: (id: string) => APIUtil.isStudy(id),
    getSchemaId: (item: TagEditorItem) => (isStudy(item) ? item.schema.id : null),
    getFields: (item: StudyListingGraphQL) => item.tags,
  },
];

class TagEditorDropdown extends React.PureComponent<
  TagEditorDropdownProps & QueriedProps,
  TagEditorDropdownState
> {
  static defaultProps = {
    disabled: false,
  };

  override state: TagEditorDropdownState = {
    isOpen: false,
    tagFilter: INITIAL_TAG_FILTER,
    tagValue: INITIAL_TAG_VALUE,
    valueFocused: false,
  };

  closeIfOpen = (): void => {
    this.setState({isOpen: false});
  };

  toggleOpen = (e: BasicEvent): void => {
    this.setState({isOpen: !this.state.isOpen});
    e.stopPropagation();
  };

  getTags(): Array<TagEditorModalItem> {
    type BaseTagEditorItem = Omit<TagEditorModalItem, 'tags'>;
    type OptionalTagsItem = BaseTagEditorItem & {tags: Array<Optional<Tag>>};
    type PossiblyMissingTagsItem = MakeFieldsOptional<OptionalTagsItem, 'tags'>;

    const items: Array<PossiblyMissingTagsItem> = this.props.results.map((item) => {
      const config = CONFIGS.find((config) => config.validateItemType(item.id));
      const tags = config?.getFields(item);
      const schemaId = config?.getSchemaId(item);
      return {
        id: item.id,
        tags,
        schemaId,
      };
    });

    return items
      .filter((item): item is OptionalTagsItem => item.tags != null)
      .map((item) => ({
        schemaId: item.schemaId,
        id: item.id,
        tags: item.tags.filter((tag): tag is Tag => tag != null),
      }));
  }

  private async runTagEditorModal(
    menuItemValue: string,
    analyticsMetadata: BaseAnalyticsMetadata
  ): Promise<void> {
    const selectedIds = this.props.results.map(({id}) => id);
    const sampleStatusWarningParams: SampleStatusWarningTrackingParams | undefined = selectedIds.every((id) =>
      APIUtil.isStorable(id)
    )
      ? {...analyticsMetadata, actionAttempted: 'update_fields', sampleIds: selectedIds}
      : undefined;
    const tagEditorModalProps = {
      tagName: menuItemValue,
      selectedItems: this.getTags(),
      sampleStatusWarningParams,
    };
    const tagModalResult: {newTag: Tag; selectedStorableIds?: Array<string>} | null = await runModal(
      TagEditorModal,
      tagEditorModalProps
    );
    if (tagModalResult) {
      const {newTag, selectedStorableIds} = tagModalResult;
      this.props.onSubmit(newTag, selectedStorableIds);
    }
  }

  private async runQuantityEditorModal(analyticsMetadata: AnalyticsMetadataForContext): Promise<void> {
    const quantityEditorModalProps = {
      containerIds: this.props.results.map((containerSummary) => containerSummary.id),
      onRequestClose: noop,
      analyticsMetadata,
    };
    const quantityModalResult: MeasurementEditorModalSubmitArgs | null = await runModal(
      QuantityEditorModal,
      quantityEditorModalProps
    );
    if (quantityModalResult) {
      const {newMeasurement, selectedContainerIds} = quantityModalResult;
      const containersWithQuantities = selectedContainerIds.map((containerId) => ({
        id: containerId,
        quantity: newMeasurement,
      }));
      await bulkUpdateContainerPropertiesWithDataMods(containersWithQuantities);
    }
  }

  private async runConcentrationEditorModal(analyticsMetadata: AnalyticsMetadataForContext): Promise<void> {
    const concentrationModalResult: MeasurementEditorModalSubmitArgs | null = await runModal(
      ConcentrationEditorModal,
      {
        containerIds: this.props.results.map((containerSummary) => containerSummary.id),
        onRequestClose: noop,
        analyticsMetadata,
      }
    );
    if (concentrationModalResult) {
      const {newMeasurement, selectedContainerIds} = concentrationModalResult;
      // TODO(BNCH-94075) We should have friendlier handling for trying to update inaccessible contained items
      // This matches the pre-existing behavior though - errors on the backend
      const containersWithConcentrations: Array<UpdateContainerContentConcentrationsParam> =
        this.props.containerInfos
          .filter((containerInfo) => selectedContainerIds.includes(containerInfo.id))
          .flatMap((containerInfo) =>
            getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(containerInfo.contents).map(
              ({containedItem}) => ({
                containerId: containerInfo.id,
                containedItemId: getAccessibleOrInaccessibleId(containedItem),
                concentration: newMeasurement,
              })
            )
          );
      await bulkUpdateContainerContentConcentrationsWithDataMods(containersWithConcentrations);
    }
  }

  openModal = async (menuItemValue: string | symbol): Promise<void> => {
    this.props.onClickTracking?.(
      typeof menuItemValue === 'string' ? 'Tag' : menuItemValue.description || menuItemValue.toString()
    );
    const eventGroupId = Analytics.makeAnalyticsEventId();
    const analyticsMetadata = {actionOrigin: this.props.actionOrigin, eventGroupId};
    if (typeof menuItemValue === 'string') {
      await this.runTagEditorModal(menuItemValue, analyticsMetadata);
    } else if (menuItemValue === CONCENTRATION_MENU_ITEM_VALUE) {
      await this.runConcentrationEditorModal(analyticsMetadata);
    } else {
      const bulkUpdateSCRProps = {
        items: this.props.containerInfos.map(
          (info) =>
            ({
              ...info,
              permissions: {
                ...info.permissions,
                canEditRestrictedSampleUsers: Boolean(info.permissions.canEditRestrictedSampleUsers),
                canRestrictSamples: Boolean(info.permissions.canRestrictSamples),
                canUnrestrictSamples: Boolean(info.permissions.canUnrestrictSamples),
              },
            }) satisfies ItemWithSampleRolesAndPermissions
        ),
        analyticsMetadata,
      };
      if (menuItemValue === QUANTITY_MENU_ITEM_VALUE) {
        await this.runQuantityEditorModal(analyticsMetadata);
      } else if (menuItemValue === SAMPLE_OWNERS_MENU_ITEM_VALUE) {
        await runModal(BulkUpdateSampleOwnersModal, bulkUpdateSCRProps);
      } else if (menuItemValue === RESTRICTED_SAMPLE_USERS_MENU_ITEM_VALUE) {
        await runModal(BulkUpdateRestrictedSampleUsersModal, bulkUpdateSCRProps);
      } else if (menuItemValue === SAMPLE_RESTRICTION_STATUS_MENU_ITEM_VALUE) {
        await runModal(BulkUpdateRestrictionStatusModal, bulkUpdateSCRProps);
      } else if (menuItemValue === FREEZE_THAW_MENU_ITEM_VALUE) {
        await runModal(BulkUpdateFreezeThawCountModal, {
          items: this.props.containerInfos.map(({id, freezeThawInfo}) => ({
            id,
            freezeThawInfo,
          })),
          analyticsMetadata,
        });
      } else if (menuItemValue === EXPIRATION_DATE_OVERRIDE_MENU_ITEM_VALUE) {
        await runModal(BulkUpdateExpirationDateOverrideModal, {
          items: this.props.containerInfos.map(({id, expirationInfo}) => ({
            id,
            expirationInfo,
          })),
          analyticsMetadata,
        });
      }
    }
  };

  allItemsHaveSameSchema(items: Array<TagEditorItem>): boolean {
    return items.every((item) =>
      CONFIGS.some(({getSchemaId}) => {
        const schemaId = getSchemaId(item);
        return schemaId != null && schemaId === getSchemaId(this.props.results[0]);
      })
    );
  }

  allItemsHaveNoSchema(items: Array<TagEditorItem>): boolean {
    return items.every((item) => CONFIGS.every(({getSchemaId}) => getSchemaId(item) == null));
  }

  getIsDisabledAndTooltipTitle(): {isDisabled: boolean; tooltipTitle: string} {
    let isDisabled = true;
    let tooltipTitle;

    const unsupportedItems = this.props.results.filter((item) =>
      CONFIGS.every(({validateItemType}) => !validateItemType(item.id))
    );

    if (!this.props.results.length) {
      tooltipTitle = 'Select items to edit properties';
    } else if (
      unsupportedItems.length &&
      unsupportedItems.length < this.props.results.length &&
      unsupportedItems.every((item) => this.props.itemHasAlternateEditor?.(item) ?? false)
    ) {
      tooltipTitle = 'Select only one item type to edit properties';
    } else if (unsupportedItems.length) {
      tooltipTitle = `Cannot edit properties for items of type ${typeToName(
        APIUtil.apiIdToType(unsupportedItems[0].id)
      )}`;
    } else if (
      this.props.results.some(
        (item) =>
          // Rely on `tagsEditable` if the key exists, otherwise `writable`
          (item.permissions.hasOwnProperty('tagsEditable') &&
            !(item as unknown as {permissions: {tagsEditable: boolean}}).permissions.tagsEditable) ||
          !item.permissions.writable
      )
    ) {
      tooltipTitle = 'You do not have permission to edit some of the selected items';
    } else {
      isDisabled = false;
      tooltipTitle = this.props.tooltipTitleWhenSelectable ?? 'Edit property...';
    }
    return {isDisabled, tooltipTitle};
  }

  private getSchemaFieldOptionDatas(tagNamesAndTypes: TagTypesByName): Array<OptionData> {
    const tagNames = naturalSort(Object.keys(tagNamesAndTypes));
    return tagNames.map((name) => getOptionDataFromTag(tagNamesAndTypes, name));
  }

  private getSampleRoleOptionDatas(): Array<OptionData> {
    const sampleStatusItems: Array<SampleStatusItem & {name: string}> = this.props.results.map((item) => {
      const containerInfo = this.props.containerInfos.find((containerInfo) => containerInfo.id === item.id);
      if (containerInfo != null) {
        return {
          ...item,
          ...omitKey(containerInfo, 'permissions'),
          permissions: {
            ...containerInfo.permissions,
            canEditRestrictedSampleUsers: Boolean(containerInfo.permissions.canEditRestrictedSampleUsers),
            canRestrictSamples: Boolean(containerInfo.permissions.canRestrictSamples),
            canUnrestrictSamples: Boolean(containerInfo.permissions.canUnrestrictSamples),
          },
        };
      } else {
        return {
          ...item,
          permissions: {
            ...item.permissions,
            movable: Boolean((item as {permissions: {movable: boolean}}).permissions.movable),
            tagsEditable: Boolean((item as {permissions: {tagsEditable: boolean}}).permissions.tagsEditable),
            owner: (item as {permissions: {owner: boolean | null}}).permissions.owner ?? undefined,
            canAccept: (item as {permissions: {canAccept: boolean}}).permissions.canAccept ?? undefined,
            canReject: (item as {permissions: {canReject: boolean}}).permissions.canReject ?? undefined,
            canRetract: (item as {permissions: {canRetract: boolean}}).permissions.canRetract ?? undefined,
            canSendForReview:
              (item as {permissions: {canSendForReview: boolean}}).permissions.canSendForReview ?? undefined,
            canReassignRlcReviewer:
              (item as {permissions: {canReassignRlcReviewer: boolean}}).permissions.canReassignRlcReviewer ??
              undefined,
          },
        };
      }
    });
    const disabledMessageProps = {
      sampleStatusItems,
      isLoading: this.props.isQueryLoading,
    };
    const restrictionStatusDisabledMessage =
      getUpdateSampleRestrictionStatusDisabledMessage(disabledMessageProps);
    const sampleOwnersDisabledMessage = getUpdateSampleOwnersDisabledMessage(disabledMessageProps);
    const restrictedSampleUsersDisabledMessage =
      getUpdateRestrictedSampleUsersDisabledMessage(disabledMessageProps);
    return [
      ...(hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
        ? [
            {
              name: 'Restriction status',
              value: SAMPLE_RESTRICTION_STATUS_MENU_ITEM_VALUE,
              disabled: restrictionStatusDisabledMessage != null,
              disabledMessage: restrictionStatusDisabledMessage ?? undefined,
            },
          ]
        : []),
      {
        name: getRestrictedSampleUsersHeaderText(),
        value: RESTRICTED_SAMPLE_USERS_MENU_ITEM_VALUE,
        disabled: restrictedSampleUsersDisabledMessage != null,
        disabledMessage: restrictedSampleUsersDisabledMessage ?? undefined,
      },
      {
        name: 'Sample owners',
        value: SAMPLE_OWNERS_MENU_ITEM_VALUE,
        disabled: sampleOwnersDisabledMessage != null,
        disabledMessage: sampleOwnersDisabledMessage ?? undefined,
      },
    ];
  }

  private getConcentrationDisabledMessage(
    containers: Array<TagEditorItem>,
    others: Array<TagEditorItem>
  ): string | undefined {
    const genericMessage = this.getContainerQueryDependentDisabledMessage(
      containers,
      others,
      'concentration'
    );
    if (genericMessage != null) {
      return genericMessage;
    }
    const containersWithNotExactlyOneContent = this.props.containerInfos.filter(
      (container) => container.contents.length !== 1
    );
    if (containersWithNotExactlyOneContent.length > 0) {
      return `You can't edit concentration because ${this.getItemsDisplayString(
        containersWithNotExactlyOneContent
      )} ${pluralize(
        containersWithNotExactlyOneContent.length,
        "doesn't",
        "don't"
      )} contain exactly one item`;
    }
    return undefined;
  }

  private getItemsDisplayString(items: Array<{name: string}>): string {
    return abbreviateList(
      items.map((item) => `"${item.name}"`),
      3
    );
  }

  private getContainerQueryDependentDisabledMessage(
    containers: Array<TagEditorItem>,
    others: Array<TagEditorItem>,
    propertyName: string
  ): string | undefined {
    if (others != null && others.length > 0) {
      return `You can't edit ${propertyName} because ${this.getItemsDisplayString(others)} ${
        others.length === 1 ? 'is not a container' : 'are not containers'
      }`;
    } else if (this.props.isQueryLoading || this.props.containerInfos.length !== containers.length) {
      // The latter case is for when you have the dropdown open & then select _more_ items - the query result
      // will temporarily be out of date.
      // At this point nothing should be inaccessible, so it's safe to just return "Loading..." as the message
      return 'Loading...';
    }
    return undefined;
  }

  private getValidContentsDisabledMessage(
    containers: Array<TagEditorItem>,
    others: Array<TagEditorItem>,
    property: 'freezeThawInfo' | 'expirationInfo'
  ): string | undefined {
    const containersWithoutTrackingEnabled = this.props.containerInfos.filter(
      (container) => !container[property]?.isTrackingEnabled
    );
    const genericMessage = this.getContainerQueryDependentDisabledMessage(
      containers,
      others,
      property === 'freezeThawInfo' ? 'freeze/thaw count' : 'expiration date override'
    );
    if (genericMessage != null) {
      return genericMessage;
    }

    if (containersWithoutTrackingEnabled.length > 0) {
      return `${
        property === 'freezeThawInfo' ? 'Freeze/thaw' : 'Expiration'
      } tracking is not enabled for the contents of ${this.getItemsDisplayString(
        containersWithoutTrackingEnabled
      )}`;
    }
    return undefined;
  }

  private getContainerPropertyOptionDatas(): Array<OptionData> {
    const {containers, others} = groupBy(this.props.results, (item) =>
      APIUtil.isContainer(item.id) ? 'containers' : 'others'
    );
    if (containers == null || containers.length === 0) {
      return [];
    }

    const quantityDisabledMessage =
      others != null && others.length > 0
        ? `You can't edit quantity because ${this.getItemsDisplayString(others)} ${
            others.length === 1 ? 'is not a container' : 'are not containers'
          }`
        : undefined;

    const concentrationDisabledMessage = this.getConcentrationDisabledMessage(containers, others);
    const freezeThawDisabledMessage = this.getValidContentsDisabledMessage(
      containers,
      others,
      'freezeThawInfo'
    );
    const expirationDateDisabledMessage = this.getValidContentsDisabledMessage(
      containers,
      others,
      'expirationInfo'
    );

    return [
      {
        name: getQuantityDisplayString(),
        value: QUANTITY_MENU_ITEM_VALUE,
        disabled: quantityDisabledMessage != null,
        disabledMessage: quantityDisabledMessage,
        dataTestAttrs: {component: 'TagEditorDropdown', element: 'quantity'},
      },
      {
        name: 'Concentration',
        value: CONCENTRATION_MENU_ITEM_VALUE,
        disabled: concentrationDisabledMessage != null,
        disabledMessage: concentrationDisabledMessage,
        dataTestAttrs: {component: 'TagEditorDropdown', element: 'concentration'},
      },
      ...(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
        ? [
            {
              name: 'Expiration date override',
              value: EXPIRATION_DATE_OVERRIDE_MENU_ITEM_VALUE,
              disabled: expirationDateDisabledMessage != null,
              disabledMessage: expirationDateDisabledMessage,
              dataTestAttrs: {component: 'TagEditorDropdown', element: 'expirationDateOverride'},
            },
          ]
        : []),
      ...(BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW
        ? [
            {
              name: 'Freeze/thaw count',
              value: FREEZE_THAW_MENU_ITEM_VALUE,
              disabled: freezeThawDisabledMessage != null,
              disabledMessage: freezeThawDisabledMessage,
              dataTestAttrs: {component: 'TagEditorDropdown', element: 'freezeThawCount'},
            },
          ]
        : []),
      ...(hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ? this.getSampleRoleOptionDatas() : []),
    ];
  }

  getOptionDatas(tagNamesAndTypes: TagTypesByName): Array<OptionData> {
    const optionDatas: Array<OptionData> = [];
    optionDatas.push(...this.getContainerPropertyOptionDatas());
    const itemsHaveSameSchema = this.allItemsHaveSameSchema(this.props.results);
    if (itemsHaveSameSchema) {
      optionDatas.push(...this.getSchemaFieldOptionDatas(tagNamesAndTypes));
    }
    if (optionDatas.length === 0) {
      const message =
        itemsHaveSameSchema || this.allItemsHaveNoSchema(this.props.results)
          ? 'Items have no editable properties'
          : 'Items have no editable properties in common';
      return [
        {
          disabled: true,
          name: message,
          value: message,
        },
      ];
    }
    return optionDatas;
  }

  override render(): JSX.Element {
    const tagNamesAndTypes = FileBrowserUtil.tagTypesByName(this.getTags());
    const {isDisabled, tooltipTitle} = this.getIsDisabledAndTooltipTitle();
    return (
      <div className="buttonGroup">
        <Tooltip title={tooltipTitle} placement={TooltipPlacement.TOP}>
          <Button
            aria-label="Edit properties"
            buttonStyle={this.props.buttonStyle}
            disabled={isDisabled}
            icon={<BenchlingIcon icon="edit" />}
            isDropdown={true}
            onClick={this.toggleOpen}
            size={ButtonSize.SMALL}
          />
        </Tooltip>
        {this.state.isOpen && (
          <ClickOutWrapper onClickOut={this.closeIfOpen}>
            <SearchableMenu
              onSelect={this.openModal}
              optionDatas={this.getOptionDatas(tagNamesAndTypes)}
              dropLeft={this.props.dropLeft}
              dataTestAttrs={{component: 'TagEditorDropdown', element: 'menu'}}
            />
          </ClickOutWrapper>
        )}
      </div>
    );
  }
}

export default function QueryingTagSchemaEditorDropdown(props: TagEditorDropdownProps): JSX.Element {
  const containerIds = props.results.filter(({id}) => APIUtil.isContainer(id)).map(({id}) => id);
  const result = useSkippableBenchlingQuery<TagEditorDropdownQuery, TagEditorDropdownQueryVariables>(
    TAG_EDITOR_DROPDOWN_QUERY,
    {
      skip: containerIds.length === 0,
      variables: {containerIds},
    }
  );

  const containerInfos: Array<ContainerInfo> =
    result.type === QueryResultType.SUCCESS
      ? result.data.containers.filter((container): container is ContainerInfo => !isInaccessible(container))
      : [];
  return (
    <TagEditorDropdown
      {...props}
      isQueryLoading={result.type === QueryResultType.PENDING}
      containerInfos={containerInfos}
    />
  );
}
